/**
 * About Hero Section
 * Main introduction section with title and key information
 */
import { Card, CardContent, CardMedia, Typography, Grid, Stack, Link, Button } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { fadeIn, zoomIn } from "../constants/animations";
import EmailIcon from "@mui/icons-material/Email";
import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import PhoneIcon from "@mui/icons-material/Phone";

const LeadDeveloper = "/images/carlos-avilez.webp";

export const HeroSection = () => {
  const { t } = useTranslation();

  return (
    <motion.div initial="hidden" animate="visible" variants={fadeIn}>
      <Card className="glass-effect" sx={{ p: 3, boxShadow: 3 }}>
        <CardContent>
          <Typography
            variant="h4"
            component="h1"
            gutterBottom
            className="animated-gradient-text"
          >
            {t("about.heading")}
          </Typography>

          <Grid container spacing={4} sx={{ mb: 3 }}>
            <Grid item xs={12}>
              <motion.div variants={zoomIn} initial="hidden" animate="visible">
                <Card
                  className="glass-effect"
                  sx={{
                    transition: "all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
                    "&:hover": {
                      transform: "translateY(-8px)",
                      boxShadow: "0 12px 40px rgba(0, 0, 0, 0.2)",
                    },
                  }}
                >
                  <CardMedia
                    component="img"
                    height="500"
                    image={LeadDeveloper}
                    alt="Carlos A Avilez J."
                  />
                  <CardContent>
                    <Typography variant="h6" align="center">
                      {t("about.name")}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" align="center">
                      {t("about.role")}
                    </Typography>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          </Grid>

          <motion.div variants={fadeIn}>
            <Typography variant="body1" paragraph>
              {t("about.intro")}
            </Typography>
          </motion.div>

          <motion.div variants={fadeIn}>
            <Typography
              variant="h5"
              gutterBottom
              className="animated-gradient-text"
            >
              {t("about.whatIDo.title")}
            </Typography>
            <Typography variant="body1" paragraph>
              {t("about.whatIDo.description")}
            </Typography>
          </motion.div>

          <motion.div variants={fadeIn}>
            <Typography
              variant="h5"
              gutterBottom
              className="animated-gradient-text"
            >
              {t("about.whyWorkWithMe.title")}
            </Typography>
            <Typography variant="body1" paragraph>
              {t("about.whyWorkWithMe.point1")}
            </Typography>
            <Typography variant="body1" paragraph>
              {t("about.whyWorkWithMe.point2")}
            </Typography>
            <Typography variant="body1" paragraph>
              {t("about.whyWorkWithMe.point3")}
            </Typography>
            <Typography variant="body1" paragraph>
              {t("about.whyWorkWithMe.point4")}
            </Typography>
          </motion.div>

          <motion.div variants={fadeIn}>
            <Typography
              variant="h5"
              gutterBottom
              className="animated-gradient-text"
            >
              {t("about.letsBuild.title")}
            </Typography>
            <Typography variant="body1" paragraph>
              {t("about.letsBuild.description")}
            </Typography>
          </motion.div>

          {/* Contact Information */}
          <Stack spacing={2} sx={{ mt: 3 }}>
            <motion.div whileHover={{ scale: 1.1 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <EmailIcon color="primary" />
                <Typography variant="body1">{t("about.contact.email")}</Typography>
              </Stack>
            </motion.div>

            <motion.div whileHover={{ scale: 1.1 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <FacebookIcon color="primary" />
                <Link
                  href="https://www.facebook.com/profile.php?id=61569313564630"
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="hover"
                >
                  {t("about.contact.facebook")}
                </Link>
              </Stack>
            </motion.div>

            <motion.div whileHover={{ scale: 1.1 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <InstagramIcon color="primary" />
                <Link
                  href="https://www.instagram.com/itenginesparks/"
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="hover"
                >
                  {t("about.contact.instagram")}
                </Link>
              </Stack>
            </motion.div>

            <motion.div whileHover={{ scale: 1.1 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <PhoneIcon color="primary" />
                <Typography variant="body1">{t("about.contact.phone")}</Typography>
              </Stack>
            </motion.div>

            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Button
                component={RouterLink}
                to="/contact"
                variant="contained"
                color="primary"
                sx={{ mt: 2 }}
              >
                {t("about.contact.contactButton")}
              </Button>
            </motion.div>
          </Stack>
        </CardContent>
      </Card>
    </motion.div>
  );
};
